/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

/**
 * useBlockCache - Block Content Caching and Persistence
 *
 * Manages caching of block tab content, metadata, and modification status.
 * Provides operations for storing, retrieving, and tracking block data changes.
 *
 * Note: This hook operates at the context provider level and doesn't use context
 * to avoid circular dependencies.
 */

import * as monaco from 'monaco-editor';
import React, { useRef } from 'react';

import { DEBUG_TAB_MODIFICATION } from '../context/TabManagerContext';
import { findTabById, getTabKey, Tab } from '../helpers/codeEditorHelpers';
import { FunctionMetadata } from '../types/LiveAgentEditorTypes';

import { CachedTab } from './types';

import { Step as UIStep } from '@/pages/projects/LiveAgentTypes';

export const useBlockCache = () => {
    /** Local cache storage */
    const blockCacheRef = useRef<Map<string, CachedTab>>(new Map());

    /** Retrieve cached tab data by key */
    const getCachedTab = (key: string): CachedTab | undefined => {
        return blockCacheRef.current.get(key);
    };

    /** Store tab data in cache */
    const setCachedTab = (key: string, cachedTab: CachedTab) => {
        blockCacheRef.current.set(key, cachedTab);
    };

    /** Remove tab from cache */
    const deleteCachedTab = (key: string) => {
        blockCacheRef.current.delete(key);
    };

    /** Mark a tab as saved (clear modification indicators) */
    const markTabAsSaved = (
        tabKey: string,
        contentRef: React.MutableRefObject<Map<string, string>>,
        originalContentRef: React.MutableRefObject<Map<string, string>>,
        setTabs?: React.Dispatch<React.SetStateAction<Tab[]>>,
        updateTabsRef?: (newTabs: Tab[]) => void,
    ) => {
        const cachedTab = blockCacheRef.current.get(tabKey);
        if (cachedTab) {
            // Update original content to current content in cache
            const tabId = tabKey === 'main' ? 'main' : `block-${tabKey}`;
            const currentContent = contentRef.current.get(tabId);
            if (DEBUG_TAB_MODIFICATION) {
                // eslint-disable-next-line no-console
                console.log(`[TAB_MODIFICATION] Setting cached tab isModified = false (markTabAsSaved)`, {
                    tabKey,
                    reason: 'tab marked as saved',
                    location: 'useBlockCache.markTabAsSaved',
                });
            }
            blockCacheRef.current.set(tabKey, {
                ...cachedTab,
                originalCode: currentContent || cachedTab.code,
                isModified: false,
            });
        }

        // Update original content to current content when saved
        const tabId = tabKey === 'main' ? 'main' : `block-${tabKey}`;
        const currentContent = contentRef.current.get(tabId);
        if (currentContent !== undefined) {
            originalContentRef.current.set(tabId, currentContent);
        }

        // Update visual tab state to clear modification indicator
        if (setTabs && updateTabsRef) {
            setTabs((prevTabs) => {
                const updatedTabs = prevTabs.map((tab) => {
                    if (tab.id === tabId) {
                        if (DEBUG_TAB_MODIFICATION) {
                            // eslint-disable-next-line no-console
                            console.log(`[TAB_MODIFICATION] Setting tab.isModified = false (markTabAsSaved)`, {
                                tabId: tab.id,
                                reason: 'tab marked as saved',
                                location: 'useBlockCache.markTabAsSaved.setTabs',
                            });
                        }
                        return { ...tab, isModified: false };
                    }
                    return tab;
                });
                updateTabsRef(updatedTabs);
                return updatedTabs;
            });
        }
    };

    /** Check if a tab has been modified */
    const isTabModified = (tabKey: string): boolean => {
        const cachedTab = blockCacheRef.current.get(tabKey);
        return cachedTab?.isModified || false;
    };

    /** Get list of all modified tab keys */
    const getAllModifiedTabs = (
        editorRef?: React.MutableRefObject<monaco.editor.IStandaloneCodeEditor | undefined>,
        activeTabIdRef?: React.MutableRefObject<string>,
        tabsRef?: React.MutableRefObject<Tab[]>,
        tabContentRef?: React.MutableRefObject<Map<string, string>>,
        tabOriginalContentRef?: React.MutableRefObject<Map<string, string>>,
        getBlockSteps?: () => UIStep[],
        enrichBlockSteps?: (inputSteps: UIStep[]) => UIStep[],
    ): string[] => {
        // First, cache the current tab if it's a block tab and parameters are provided
        if (
            editorRef &&
            activeTabIdRef &&
            tabsRef &&
            tabContentRef &&
            tabOriginalContentRef &&
            getBlockSteps &&
            enrichBlockSteps
        ) {
            const currentTabId = activeTabIdRef.current;
            // Only cache if current tab is a block tab (not main)
            if (currentTabId && currentTabId !== 'main' && currentTabId.startsWith('block-')) {
                cacheCurrentTab(
                    editorRef,
                    activeTabIdRef,
                    tabsRef,
                    tabContentRef,
                    tabOriginalContentRef,
                    getBlockSteps,
                    enrichBlockSteps,
                );
            }
        }

        const modifiedTabs: string[] = [];

        // Check if main tab is modified (if we have access to tabs state)
        if (tabsRef) {
            for (const tab of tabsRef.current) {
                if (tab.isModified) {
                    modifiedTabs.push(getTabKey(tab.id));
                }
            }
        }

        return modifiedTabs;
    };

    /**
     * Cache current tab content and metadata
     * Takes parameters directly since this hook operates at provider level
     */
    const cacheCurrentTab = (
        editorRef: React.MutableRefObject<monaco.editor.IStandaloneCodeEditor | undefined>,
        activeTabIdRef: React.MutableRefObject<string>,
        tabsRef: React.MutableRefObject<Tab[]>,
        tabContentRef: React.MutableRefObject<Map<string, string>>,
        tabOriginalContentRef: React.MutableRefObject<Map<string, string>>,
        getBlockSteps: () => UIStep[],
        enrichBlockSteps: (inputSteps: UIStep[]) => UIStep[],
    ) => {
        if (!editorRef.current || !activeTabIdRef.current) return;

        const activeTab = findTabById(tabsRef.current, activeTabIdRef.current);
        if (!activeTab) return;

        const key = getTabKey(activeTabIdRef.current);
        const code = editorRef.current.getValue();
        const originalCode = tabOriginalContentRef.current.get(activeTabIdRef.current) || '';

        // Update tabContentRef with current editor content so applyTabContent can retrieve it
        tabContentRef.current.set(activeTabIdRef.current, code);

        let cacheSteps: UIStep[] = [];
        try {
            cacheSteps = getBlockSteps();
            cacheSteps = enrichBlockSteps(cacheSteps);
        } catch (e) {
            cacheSteps = [];
        }

        const isModified = code !== originalCode;
        if (DEBUG_TAB_MODIFICATION) {
            // eslint-disable-next-line no-console
            console.log(`[TAB_MODIFICATION] Setting cached tab isModified = ${isModified} (cacheCurrentTab)`, {
                key,
                tabId: activeTabIdRef.current,
                isModified,
                reason: isModified ? 'code differs from original' : 'code matches original',
                location: 'useBlockCache.cacheCurrentTab',
                codeLength: code.length,
                originalCodeLength: originalCode.length,
            });
        }
        setCachedTab(key, {
            code,
            originalCode,
            steps: cacheSteps,
            metadataManager: activeTab.metadataManager,
            decorationManager: activeTab.decorationManager,
            isModified,
        });
    };

    /**
     * Update modification status indicators for current tab
     * Takes parameters directly since this hook operates at provider level
     *
     * For main tab: considers both code changes AND step action changes (when steps are executed)
     * For block tabs: only considers code changes (current behavior)
     */
    const updateCurrentTabModificationStatus = (
        editorRef: React.MutableRefObject<monaco.editor.IStandaloneCodeEditor | undefined>,
        activeTabIdRef: React.MutableRefObject<string>,
        tabOriginalContentRef: React.MutableRefObject<Map<string, string>>,
        setTabs: React.Dispatch<React.SetStateAction<Tab[]>>,
        updateTabsRef: (newTabs: Tab[]) => void,
        tabsRef?: React.MutableRefObject<Tab[]>,
    ) => {
        if (!editorRef.current) return;

        const currentContent = editorRef.current.getValue();
        const currentTabId = activeTabIdRef.current;
        const originalContent = tabOriginalContentRef.current.get(currentTabId) || '';

        if (tabsRef?.current.find((tab) => tab.id === currentTabId)?.isModified) {
            return;
        }

        let isModified = false;

        if (currentTabId === 'main' && tabsRef) {
            // For main tab: check both code changes AND step action changes
            const codeChanged = currentContent !== originalContent;

            // Check if any steps have been executed (have actions)
            const activeTab = tabsRef.current.find((tab: Tab) => tab.id === currentTabId);
            let hasExecutedSteps = false;

            if (activeTab?.metadataManager) {
                const metadata = activeTab.metadataManager.getMetadata();
                hasExecutedSteps = Object.values(metadata).some(
                    (meta: FunctionMetadata) => meta.executed && meta.result !== 'UNKNOWN',
                );
            }

            isModified = codeChanged || hasExecutedSteps;
        } else {
            // For block tabs: only check code changes (current behavior)
            isModified = currentContent !== originalContent;
        }

        setTabs((prevTabs) => {
            const updatedTabs = prevTabs.map((tab) => {
                if (tab.id === currentTabId) {
                    if (DEBUG_TAB_MODIFICATION) {
                        // eslint-disable-next-line no-console
                        console.log(
                            `[TAB_MODIFICATION] Setting tab.isModified = ${isModified}` +
                                ` (updateCurrentTabModificationStatus)`,
                            {
                                tabId: currentTabId,
                                isModified,
                                reason:
                                    currentTabId === 'main'
                                        ? isModified
                                            ? 'code changed or steps executed'
                                            : 'code matches original and no executed steps'
                                        : isModified
                                          ? 'code differs from original'
                                          : 'code matches original',
                                location: 'useBlockCache.updateCurrentTabModificationStatus',
                                isMainTab: currentTabId === 'main',
                            },
                        );
                    }
                    return { ...tab, isModified };
                }
                return tab;
            });
            updateTabsRef(updatedTabs);
            return updatedTabs;
        });
    };

    return {
        blockCacheRef,
        getCachedTab,
        setCachedTab,
        deleteCachedTab,
        markTabAsSaved,
        isTabModified,
        getAllModifiedTabs,
        cacheCurrentTab,
        updateCurrentTabModificationStatus,
    };
};
